import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError, AxiosResponse, AxiosResponseHeaders } from "axios";
import { Thunk } from "../../store";
import {
  BackendActualizarDisponibilidad,
  getCurrentDomain,
} from "../../../apis/backend/Api";
import mock_Json from "../../../dataJson/actualizarDisponibilidad.json";
import { formatearMensajeBackend } from "../../../apis/helper/helperApi";
import { TypeStatus } from "../../helpers/constantes";

interface State {
  actualizarDisponibilidadBackend: any[];
  status: TypeStatus;
  isLoading: boolean;
  Success: boolean;
  Mensaje: string | null;
  error: string | null;
  disponible: boolean;
  disponibleGuardia: boolean;
}

const initialState: State = {
  actualizarDisponibilidadBackend: [],
  status: "idle",
  isLoading: false,
  Success: false,
  Mensaje: "",
  error: null,
  disponible: false,
  disponibleGuardia: false,
};

interface defaultResponse {
  data: any;
  status: number;
  statusText: string;
  headers: AxiosResponseHeaders | {};
  config: any;
  request?: any;
}
const defaultResp: defaultResponse = {
  data: undefined,
  status: 0,
  statusText: "",
  headers: {},
  config: undefined,
};
const actualizarDisponibilidadSlice = createSlice({
  name: "actualizarDisponibilidadBackend",
  initialState,
  reducers: {
    setActualizarDisponibilidadBackend: (
      state,
      action: PayloadAction<any[]>,
    ) => {
      state.actualizarDisponibilidadBackend = action.payload;
    },
    getActualizarDisponibilidadBackend: (
      state,
      action: PayloadAction<any[]>,
    ) => {
      state.actualizarDisponibilidadBackend = action.payload;
    },
    setClearActualizarDisponibilidadBackend: (
      state,
      action: PayloadAction<any[]>,
    ) => {
      state = initialState;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setStatus: (state, action: PayloadAction<TypeStatus>) => {
      state.status = action.payload;
    },
    setResult: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setSuccess: (state, action: PayloadAction<boolean>) => {
      state.Success = action.payload;
    },
    setMensaje: (state, action: PayloadAction<string>) => {
      state.Mensaje = action.payload;
    },
    setDisponible: (state, action: PayloadAction<boolean>) => {
      state.disponible = action.payload;
    },
    setDisponibleGuardia: (state, action: PayloadAction<boolean>) => {
      state.disponibleGuardia = action.payload;
    },
  },
});

export const setBackendActualizarDisponibilidad =
  (disponible: boolean, disponibleGuardia: boolean): Thunk =>
  async (dispatch): Promise<AxiosResponse | AxiosError> => {
    dispatch(setIsLoading(true));
    try {
      const response: AxiosResponse = await BackendActualizarDisponibilidad(
        disponible,
        disponibleGuardia,
      );

      const isError =
        response.data?.Success === false ||
        (response.data?.ResultadoSolicitud !== undefined &&
          response.data?.ResultadoSolicitud !== 200);

      if (isError) {
        dispatch(setStatus("failed"));
        dispatch(setSuccess(false));
      } else {
        dispatch(setActualizarDisponibilidadBackend(response.data.Data));
        dispatch(setStatus("succeeded"));
        dispatch(setSuccess(true));
        dispatch(setDisponible(disponible));
        dispatch(setDisponibleGuardia(disponibleGuardia));
      }

      dispatch(setIsLoading(false));
      dispatch(setMensaje(formatearMensajeBackend(response.data.Mensaje)));

      return response.data;
    } catch (error: any) {
      dispatch(setIsLoading(false));
      dispatch(setSuccess(false));
      dispatch(setStatus("failed"));

      if (error.response?.status === 401) {
        dispatch(setStatus("noAutorice"));
        dispatch(
          setMensaje(
            formatearMensajeBackend(
              "Tu sesión ha expirado. Vuelve a iniciar sesión",
            ),
          ),
        );
      } else if (error.response) {
        // Hay respuesta del servidor, pero es otro error (400, 422, 500, etc.)
        console.error(
          "Error de servidor:",
          error.response.status,
          error.response.data,
        );

        const errorData = error.response.data;
        const mensajeBackend = errorData?.Mensaje;
        const resultadoSolicitud = errorData?.ResultadoSolicitud;

        // Si el backend devolvió un objeto de respuesta con ResultadoSolicitud !== 200 o Success === false
        if (
          mensajeBackend &&
          (resultadoSolicitud !== 200 || errorData?.Success === false)
        ) {
          dispatch(setMensaje(formatearMensajeBackend(mensajeBackend)));
        } else {
          dispatch(
            setMensaje(
              `Error ${error.response.status}: Ocurrió un problema en el servidor.`,
            ),
          );
        }
      } else if (error.request) {
        // La solicitud fue enviada, pero no hubo respuesta
        console.error("Error de conexión:", error.message);
        dispatch(
          setMensaje(
            "No se pudo conectar al servidor. Por favor, revisa tu conexión a internet o tu VPN.",
          ),
        );
      } else {
        // Es un error de red (DNS, etc.) o un error de configuración de la solicitud
        console.error("Error de red o solicitud:", error.message);
        dispatch(
          setMensaje(
            "Ocurrió un problema con la red. Por favor, verifica tu conexión.",
          ),
        );
      }
      return error as AxiosError;
    } finally {
      if (getCurrentDomain() === "localhost") {
        let response: AxiosResponse = defaultResp;
        response.data = mock_Json;

        const isErrorMock =
          response.data?.Success === false ||
          (response.data?.ResultadoSolicitud !== undefined &&
            response.data?.ResultadoSolicitud !== 200);

        if (isErrorMock) {
          dispatch(setStatus("failed"));
          dispatch(setSuccess(false));
        } else {
          dispatch(setActualizarDisponibilidadBackend(response.data.Data));
          dispatch(setStatus("succeeded"));
          dispatch(setSuccess(true));
          dispatch(setDisponible(disponible));
          dispatch(setDisponibleGuardia(disponibleGuardia));
        }

        dispatch(setMensaje(formatearMensajeBackend(response.data.Mensaje)));

        await import("../../../dataJson/401.json")
          .then((mock401) => {
            if (mock401.status === 401) {
              dispatch(setStatus("noAutorice"));
              dispatch(
                setMensaje(
                  formatearMensajeBackend(
                    "Tu sesión ha expirado. Vuelve a iniciar sesión",
                  ),
                ),
              );
            }
          })
          .catch(() => {});
      }
      dispatch(setIsLoading(false));
    }
  };

export const setBackendClearActualizarDisponibilidad =
  (): Thunk =>
  async (dispatch): Promise<AxiosResponse | AxiosError> => {
    dispatch(setIsLoading(true));
    try {
      let response: AxiosResponse = defaultResp;
      dispatch(setActualizarDisponibilidadBackend([]));
      dispatch(setIsLoading(false));
      dispatch(setStatus("idle"));
      dispatch(setSuccess(false));
      dispatch(setMensaje(""));
      return response.data;
    } catch (error: any) {
      dispatch(setIsLoading(false));
      dispatch(setStatus("failed"));
      return error as AxiosError;
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const {
  setActualizarDisponibilidadBackend,
  getActualizarDisponibilidadBackend,
  setClearActualizarDisponibilidadBackend,
  setIsLoading,
  setStatus,
  setDisponibleGuardia,
  setResult,
  setSuccess,
  setMensaje,
  setDisponible,
} = actualizarDisponibilidadSlice.actions;

export default actualizarDisponibilidadSlice.reducer;
